import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Pencil, Send, Rocket, CheckCircle2, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, CONTENT_TYPES, emitPostsChanged, pollAiPending } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, PlatformRow, MediaThumb, VideoPlayer } from "@/components/Bits";
import DecisionButtons, { DECISION_RING } from "@/components/DecisionButtons";
import { TranscriptBadge, TranscriptUploadButton, useTranscriptInfo } from "@/components/Transcript";
import { SaveButton, CopyButton } from "@/components/MobileTools";

export default function PostDetailDialog({ post: initial, onClose, onChanged, onEdit }) {
  const { user } = useAuth();
  const [post, setPost] = useState(initial);
  const [files, setFiles] = useState([]);
  const [thumb, setThumb] = useState(null);
  const [text, setText] = useState("");
  const isAdmin = user.role === "admin";

  useEffect(() => {
    api.get("/files", { params: { client_id: initial.client_id } }).then((r) => {
      setFiles(r.data.filter((f) => initial.media_ids.includes(f.id)));
      setThumb(r.data.find((f) => f.id === initial.thumbnail_id) || null);
    });
  }, [initial]);

  const act = async (fn, msg) => {
    try {
      const r = await fn();
      setPost(r.data);
      setText("");
      onChanged?.();
      if (msg) toast.success(msg);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const setStatus = (status, m) => act(() => api.post(`/posts/${post.id}/status`, { status, comment: text || null }), m);
  const comment = () => text.trim() && act(() => api.post(`/posts/${post.id}/comments`, { text }));
  const remove = async () => {
    if (!window.confirm("Diesen Beitrag löschen? Die Datei bleibt im Drive.")) return;
    try { await api.delete(`/posts/${post.id}`); toast.success("Beitrag gelöscht"); emitPostsChanged(); onChanged?.(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  const rewrite = async () => {
    try { await api.post(`/posts/${post.id}/rewrite-caption`); setPost({ ...post, ai_pending: true, ai_failed: false }); emitPostsChanged(); pollAiPending(post.client_id); } catch (e) { toast.error(errMsg(e)); }
  };
  const [trBusy, setTrBusy] = useState(false);
  const trInfo = useTranscriptInfo(isAdmin ? post.transcript_id : null);
  const attachTranscript = async (file) => {
    setTrBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/posts/${post.id}/transcript`, fd);
      setPost({ ...post, transcript_id: r.data.transcript.id, ai_pending: true, ai_failed: false });
      toast.success(`Transkript übernommen (${r.data.transcript.words} Wörter) – Agent schreibt die Caption`);
      emitPostsChanged(); pollAiPending(post.client_id);
    } catch (e) { toast.error(errMsg(e)); } finally { setTrBusy(false); }
  };
  useEffect(() => {
    if (!post.ai_pending) return;
    const t = setInterval(() => api.get("/posts", { params: { client_id: post.client_id } }).then((r) => { const p = r.data.find((x) => x.id === post.id); if (p && !p.ai_pending) setPost(p); }).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [post.ai_pending, post.id, post.client_id]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="dark text-foreground w-[96vw] max-w-2xl max-h-[92vh] overflow-y-auto scroll-thin rounded-xl p-4 sm:p-6" data-testid="post-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 pr-6 text-left leading-snug">{post.title || "Ohne Titel"} <StatusBadge status={post.status} /></DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <span className="font-mono">{post.scheduled_at ? new Date(post.scheduled_at).toLocaleString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "Nicht geplant"}</span>
          <PlatformRow platforms={post.platforms} />
          <span>{CONTENT_TYPES[post.content_type] || "Foto"}</span>
        </div>
        {files.length > 0 && (
          <div className="flex gap-3 overflow-x-auto scroll-thin p-1">
            {files.map((f) => (
              <div key={f.id} className={`shrink-0 rounded-lg overflow-hidden ring-4 transition-all ${DECISION_RING[post.status] || "ring-transparent"}`} data-testid="detail-media">
                {f.kind === "video"
                  ? <VideoPlayer file={f} className="h-72 sm:h-64 bg-black" />
                  : <MediaThumb file={f} className="h-72 sm:h-64 w-56 sm:w-48" />}
              </div>
            ))}
          </div>
        )}
        <div className="rounded-lg bg-white/[0.03] border border-border p-4 text-sm whitespace-pre-wrap select-text" data-testid="post-caption">
          {post.ai_pending ? <span className="text-purple-300 animate-pulse">Agent schreibt die Caption…</span> : (post.caption || <span className="text-muted-foreground">Noch keine Caption.</span>)}
          {!post.ai_pending && post.hashtags?.length > 0 && <p className="text-teal-300 mt-3">{post.hashtags.join(" ")}</p>}
          {isAdmin && !post.ai_pending && (post.ai_failed || !post.caption) && (
            <Button size="sm" variant="outline" onClick={rewrite} className="mt-3 gap-2 border-purple-500/40 text-purple-200" data-testid="rewrite-caption-button"><Wand2 className="h-4 w-4" />Caption schreiben lassen</Button>
          )}
        </div>
        {isAdmin && (
          <div className="space-y-2" data-testid="detail-transcript-section">
            <TranscriptBadge info={trInfo} />
            <TranscriptUploadButton onFile={attachTranscript} busy={trBusy || post.ai_pending} busyLabel={trBusy ? "Wird hochgeladen…" : "Agent schreibt…"} testId="detail-transcript-upload-button"
              label={post.transcript_id ? "Anderes Transkript (.txt) – Caption neu schreiben" : "Transkript (.txt) hochladen – Caption daraus schreiben"} />
          </div>
        )}

        {isAdmin && !post.ai_pending && (
          <div className="rounded-xl border border-teal-500/30 bg-teal-500/[0.05] p-3 space-y-2" data-testid="posting-kit">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-teal-400 px-1">Zum Posten</p>
            {files.map((f, i) => <SaveButton key={f.id} file={f} className="w-full" label={files.length > 1 ? `${f.kind === "video" ? "Video" : "Foto"} ${i + 1} speichern` : `${f.kind === "video" ? "Video" : "Foto"} in Galerie speichern`} />)}
            {thumb && (
              <div className="flex items-center gap-3 rounded-lg border border-amber-400/30 p-2" data-testid="thumbnail-block">
                <MediaThumb file={thumb} className="h-20 w-16 rounded-md shrink-0" />
                <div className="flex-1 min-w-0 space-y-1.5">
                  <p className="text-xs text-amber-300 font-semibold">Thumbnail</p>
                  <SaveButton file={thumb} label="Thumbnail speichern" className="w-full h-9 text-sm" color="bg-amber-400" testId="save-thumbnail-button" />
                </div>
              </div>
            )}
            {post.thumbnail_text && (
              <div className="flex items-center gap-2 rounded-lg bg-white/[0.03] border border-border px-3 py-2" data-testid="thumbnail-text">
                <p className="flex-1 text-sm"><span className="text-[10px] font-mono uppercase tracking-widest text-amber-300 block">Thumbnail-Text</span>{post.thumbnail_text}</p>
                <CopyButton text={post.thumbnail_text} label="" testId="copy-thumbnail-text-button" />
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <CopyButton text={post.caption} label="Text" testId="copy-caption-button" />
              <CopyButton text={(post.hashtags || []).join(" ")} label="Hashtags" testId="copy-hashtags-button" />
              <CopyButton text={[post.caption, (post.hashtags || []).join(" ")].filter(Boolean).join("\n\n")} label="Alles" testId="copy-all-button" />
            </div>
            {post.status === "published"
              ? <p className="h-12 rounded-lg bg-emerald-500/15 text-emerald-400 font-semibold flex items-center justify-center gap-2" data-testid="posted-badge"><CheckCircle2 className="h-5 w-5" />Gepostet{post.published_at ? ` am ${new Date(post.published_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}` : ""}</p>
              : <button onClick={() => setStatus("published", "Als gepostet abgehakt")} className="w-full h-12 rounded-lg bg-emerald-500 text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform" data-testid="mark-posted-button"><CheckCircle2 className="h-5 w-5" />Gepostet</button>}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {!isAdmin && post.status !== "published" && <div className="w-full"><DecisionButtons post={post} onDecide={(s) => setStatus(s, { approved: "Freigegeben", pending_approval: "Zurück auf offen", rejected: "Gesperrt" }[s])} /></div>}
          {isAdmin && (
            <>
              <Button variant="outline" onClick={() => onEdit ? onEdit(post) : null} disabled={!onEdit} className="gap-2" data-testid="edit-post-button"><Pencil className="h-4 w-4" />Bearbeiten</Button>
              {["draft", "changes_requested", "rejected"].includes(post.status) && <Button variant="outline" onClick={() => setStatus("pending_approval", "Zur Freigabe gesendet")} className="gap-2" data-testid="send-approval-button"><Send className="h-4 w-4" />Zur Freigabe</Button>}
              {post.status === "approved" && <Button variant="outline" onClick={() => setStatus("scheduled", "Eingeplant")} data-testid="mark-scheduled-button">Als eingeplant markieren</Button>}
              {post.status !== "published" && <Button onClick={() => setStatus("published", "Als veröffentlicht markiert")} className="bg-teal-500 hover:bg-teal-400 text-slate-950 gap-2" data-testid="mark-published-button"><Rocket className="h-4 w-4" />Veröffentlicht</Button>}
              <Button variant="outline" onClick={remove} className="gap-2 text-rose-300 border-rose-500/40 hover:bg-rose-500/10 ml-auto" data-testid="detail-delete-post-button"><Trash2 className="h-4 w-4" />Löschen</Button>
            </>
          )}
        </div>

        <div className="border-t border-border pt-4">
          <h4 className="text-sm font-medium mb-3 flex items-center gap-2"><MessageSquare className="h-4 w-4" />Kommentare</h4>
          <div className="space-y-3 mb-4" data-testid="comments-list">
            {post.comments.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Kommentare.</p>}
            {post.comments.map((c) => (
              <div key={c.id} className="text-sm">
                <span className={`font-medium ${c.role === "admin" ? "text-teal-300" : "text-amber-300"}`}>{c.user_name}</span>
                <span className="text-xs text-muted-foreground ml-2">{new Date(c.created_at).toLocaleString("de-DE")}</span>
                {c.kind !== "comment" && <span className="ml-2"><StatusBadge status={c.kind} /></span>}
                <p className="text-slate-300 mt-0.5">{c.text}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Kommentar schreiben…" className="resize-none" data-testid="comment-input" />
            <Button onClick={comment} variant="secondary" className="h-auto" data-testid="comment-submit-button">Senden</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
